import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { format } from 'date-fns'
import { dateLocale } from '@/lib/dateLocale'
import type { CalendarView } from './useCalendar'

export interface CalendarToolbarProps {
  view: CalendarView
  currentDate: Date
  isMobile: boolean
  onViewChange: (view: CalendarView) => void
  onNavigate: (amount: number) => void
  onToday: () => void
  onNew: () => void
}

export function CalendarToolbar({
  view,
  currentDate,
  isMobile,
  onViewChange,
  onNavigate,
  onToday,
  onNew,
}: CalendarToolbarProps) {
  const { t, i18n } = useTranslation()

  // 日期标题跟随界面语言（OPT-005）：PPPP/PPP 为 date-fns 本地化长格式
  const locale = dateLocale(i18n.language)
  const isCJK = ['zh-TW', 'ja', 'ko'].includes(i18n.language)
  const title = (() => {
    if (view === 'day') return format(currentDate, 'PPPP', { locale })
    if (view === 'week') {
      return format(currentDate, 'PPP', { locale })
    }
    return format(currentDate, isCJK ? 'yyyy MMMM' : 'MMMM yyyy', { locale })
  })()

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3" data-testid="calendar-toolbar">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onNavigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={onToday}>
          {t('schedule.calendar.today')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => onNavigate(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="text-lg font-semibold">{title}</div>

      <div className="flex items-center gap-2">
        {!isMobile && (
          <div className="flex items-center gap-1">
            {(['month', 'week'] as CalendarView[]).map((v) => (
              <Button
                key={v}
                variant={view === v ? 'default' : 'outline'}
                size="sm"
                data-testid={`view-tab-${v}`}
                onClick={() => onViewChange(v)}
              >
                {t(`schedule.calendar.${v}`)}
              </Button>
            ))}
          </div>
        )}
        <Button size="sm" onClick={onNew}>
          <Plus className="h-4 w-4 mr-1" />
          {t('schedule.new')}
        </Button>
      </div>
    </div>
  )
}
